import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Leaf, Sparkles, Eye, Heart, Palette, Users2, Zap, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";

const pillars = [
  {
    icon: Compass,
    title: "Confidence",
    body: "We help each person uncover their unique gifts — building belief in themselves through small, meaningful wins.",
  },
  {
    icon: Leaf,
    title: "Purpose",
    body: "We grow through collaboration. Every session is tied to something that matters to the person we're supporting.",
  },
  {
    icon: Sparkles,
    title: "Joy",
    body: "We bring joy to every step of the journey — because belonging, laughter and lightness are part of the work.",
  },
];

const visionInAction = [
  {
    icon: Eye,
    title: "Empower through awareness",
    body: "We begin with mindfulness — helping people connect deeply with themselves. Through reflection and self-awareness, participants build emotional intelligence, confidence and clarity.",
  },
  {
    icon: Heart,
    title: "Nurture the whole person",
    body: "Our supports are holistic — integrating mind, body and spirit. We encourage balanced living through healthy food, movement, creativity and quiet moments.",
  },
  {
    icon: Palette,
    title: "Cultivate creativity and expression",
    body: "From art and dance to nature exploration, we provide empowering pathways for people to explore their unique gifts and experience joy in creation.",
  },
  {
    icon: Users2,
    title: "Build community and collaboration",
    body: "Connection sits at the heart of PeerWave. Shared projects, group learning and creative collaborations foster belonging, empathy and lifelong friendships.",
  },
  {
    icon: Zap,
    title: "Inspire change and expansion",
    body: "We encourage a growth mindset rooted in curiosity, honesty and authenticity — expanding beyond limits and creating ripples of positive change.",
  },
  {
    icon: ShieldCheck,
    title: "Lead with compassion and integrity",
    body: "Everything we do is grounded in kindness, respect and honesty. We don't just support people — we walk beside them, empowering true independence.",
  },
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto container-px pt-16 pb-16 md:pt-24 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">About PeerWave</p>
            <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02]">
              The heart of
              <br />
              <span className="wave-underline">PeerWave</span>.
            </h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">
              PeerWave was founded by Laoise in the Fraser Coast community
              with one belief — that everyone deserves a place where they feel
              seen, heard and empowered to grow. Capacity building sits at the
              core of everything we do — helping people build the skills,
              confidence and connections to live the life they choose.
            </p>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="w-full rounded-[2rem] overflow-hidden ring-1 ring-black/5 shadow-lg bg-secondary">
              <img
                src="https://customer-assets.emergentagent.com/job_peerwave-landing/artifacts/7dh8m570_floating%20happily4.jpg"
                alt="A PeerWave participant floating happily in clear water"
                className="w-full h-auto block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-secondary/60 border-y border-border/60">
        <div className="max-w-7xl mx-auto container-px py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Our mission</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight">
              Confidence. Purpose. Joy.
            </h2>
            <blockquote className="mt-6 text-lg text-foreground/75 leading-relaxed border-l-2 border-primary/40 pl-5 italic">
              "At PeerWave, we empower individuals to uncover their unique
              gifts, grow through collaboration, and find belonging in
              community. Our mission is to inspire confidence, cultivate
              purpose, and bring joy to every step of the journey."
            </blockquote>
            <p className="mt-4 text-sm text-muted-foreground">
              — Laoise, Director &amp; Founder
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-5">
            {pillars.map((p) => (
              <div
                key={p.title}
                data-testid={`pillar-${p.title.toLowerCase()}`}
                className="rounded-3xl bg-card p-7 border border-border/70"
              >
                <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-medium">{p.title}</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="max-w-7xl mx-auto container-px pt-20 md:pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Our vision</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight">
              A brighter, more connected world.
            </h2>
            <blockquote className="mt-6 text-lg text-foreground/75 leading-relaxed border-l-2 border-primary/40 pl-5 italic">
              "To create a community where everyone has the opportunity to
              explore and express their unique gifts, collaborate on
              meaningful projects, and cultivate lifelong friendships. We
              aspire to inspire change, break boundaries, and unleash the
              potential of every individual to contribute to a brighter world."
            </blockquote>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {visionInAction.map((v) => (
              <div
                key={v.title}
                data-testid={`vision-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl bg-card p-6 border border-border/70 hover:border-primary/40 transition"
              >
                <div className="h-10 w-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-base font-medium">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-7xl mx-auto container-px pt-20 md:pt-28 pb-24">
        <div className="rounded-[2rem] bg-secondary/60 border border-border/70 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Meet the team</p>
            <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-medium">
              The people behind the <span className="wave-underline">wave</span>.
            </h3>
          </div>
          <Button
            asChild
            data-testid="about-team-cta"
            className="rounded-full bg-primary text-primary-foreground h-12 px-6"
          >
            <Link to="/team">
              See the team
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Vision cards close-out — page ends here now that Team has its own page */}
    </div>
  );
};

export default About;
